import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Global Error Boundary — catches any uncaught render error in the app
 * and shows a friendly "Something went wrong" screen instead of a white page.
 * Resets when the route changes (key on the wrapper) or via the action buttons.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    this.setState({ info });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ error: null, info: null });
    window.location.href = '/dashboard';
  };

  handleDismiss = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const message = error?.message || 'An unexpected error occurred.';
    const stack = info?.componentStack || error?.stack || '';

    return (
      <div
        className="min-h-screen w-full bg-background text-foreground flex items-center justify-center p-6"
        data-testid="error-boundary"
      >
        <div className="max-w-lg w-full bg-card border border-border rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="h-16 w-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-heading font-bold mb-2">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              The page hit an unexpected error. Try refreshing or go back home.
            </p>
          </div>

          <div className="bg-background/50 border border-border rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-red-400 break-words">{message}</p>
            {stack ? (
              <details className="mt-2">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  Show technical details
                </summary>
                <pre className="text-[10px] text-muted-foreground mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
                  {stack}
                </pre>
              </details>
            ) : null}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity font-medium"
              data-testid="error-boundary-reload"
            >
              <RefreshCw className="h-4 w-4" />
              Reload Page
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg border border-border bg-card hover:bg-muted transition-colors font-medium"
              data-testid="error-boundary-home"
            >
              <Home className="h-4 w-4" />
              Go to Dashboard
            </button>
          </div>

          <button
            type="button"
            onClick={this.handleDismiss}
            className="mt-3 w-full text-xs text-muted-foreground hover:text-foreground py-2"
            data-testid="error-boundary-dismiss"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
